import { api, ErroApi } from './client'

export type SituacaoDoVerbete = 'Rascunho' | 'Publicado' | 'Arquivado'

export type TipoDeRota = 'Interna' | 'Externa'

/**
 * Um pedaço de texto com no máximo uma marca.
 *
 * 🔴 O back devolve **estrutura, não HTML** — por isso a tela monta nós de React
 * e não existe `dangerouslySetInnerHTML` em lugar nenhum desta cadeia. `<script>`
 * escrito num verbete chega aqui como texto e o React o escapa sozinho.
 */
export type Pedaco = {
  marca:
    | 'texto'
    | 'negrito'
    | 'codigo'
    | 'destaque'
    | 'atencao'
    | 'perigo'
    | 'sucesso'
    | 'linkInterno'
    | 'linkExterno'
  texto: string
  destino: string | null
}

export type Celula = { pedacos: Pedaco[] }
export type Linha = { celulas: Celula[] }

export type Bloco = {
  tipo: 'titulo' | 'paragrafo' | 'lista' | 'tabela'
  texto: Pedaco[]
  linhas: Linha[]
}

export type RotaNaResposta = {
  tipo: TipoDeRota
  caminho: string
  rotulo: string

  /** Falso quando o leitor não tem a permissão da tela — o link vira inerte. */
  alcancavel: boolean

  /** Nome da permissão exigida, para dizer ao leitor o que falta. */
  exige: string | null
}

export type VerbeteNaLista = {
  id: number
  titulo: string
  categoria: string
  situacao: SituacaoDoVerbete
  ordem: number
  criadoEm: string
  atualizadoEm: string | null
  atualizadoPor: string | null
  rotas: RotaNaResposta[]
}

export type Verbete = {
  id: number
  titulo: string

  /** O texto como o autor escreveu — é o que a edição carrega. */
  conteudo: string

  /** A árvore fechada — é o que a leitura desenha. */
  blocos: Bloco[]

  categoria: string
  situacao: SituacaoDoVerbete
  ordem: number
  criadoEm: string
  atualizadoEm: string | null
  atualizadoPor: string | null
  rotas: RotaNaResposta[]
}

export type RotaDoPortal = {
  caminho: string
  nome: string
  exige: string | null
  alcancavel: boolean
}

export type PedidoDeRota = { tipo: TipoDeRota; caminho: string; rotulo: string | null }

export type PedidoDeVerbete = {
  titulo: string
  conteudo: string
  categoria: string
  ordem: number
  rotas: PedidoDeRota[]
}

export type AjudaDaRota = {
  rota: string
  total: number
  verbetes: { id: number; titulo: string; categoria: string; blocos: Bloco[] }[]
}

export const ajuda = {
  listar: (filtros?: { categoria?: string; situacao?: string; busca?: string }) => {
    const q = new URLSearchParams()
    if (filtros?.categoria) q.set('categoria', filtros.categoria)
    if (filtros?.situacao) q.set('situacao', filtros.situacao)
    if (filtros?.busca) q.set('busca', filtros.busca)

    const cauda = q.toString()
    return api.get<VerbeteNaLista[]>(`/ajuda${cauda ? `?${cauda}` : ''}`)
  },

  ler: (id: number) => api.get<Verbete>(`/ajuda/${id}`),

  rotas: () => api.get<RotaDoPortal[]>('/ajuda/rotas'),

  /** A ajuda de uma tela. O caminho vai como está na URL; o back normaliza. */
  porRota: (caminho: string) =>
    api.get<AjudaDaRota>(`/ajuda/por-rota?caminho=${encodeURIComponent(caminho)}`),

  criar: (pedido: PedidoDeVerbete) =>
    api.post<{ id: number; situacao: SituacaoDoVerbete }>('/ajuda', pedido),

  salvar: (id: number, pedido: PedidoDeVerbete) =>
    api.put<{ id: number; situacao: SituacaoDoVerbete }>(`/ajuda/${id}`, pedido),

  publicar: (id: number) =>
    api.post<{ id: number; situacao: SituacaoDoVerbete }>(`/ajuda/${id}/publicar`, {}),

  arquivar: (id: number) =>
    api.post<{ id: number; situacao: SituacaoDoVerbete }>(`/ajuda/${id}/arquivar`, {}),

  apagar: (id: number) => api.delete<void>(`/ajuda/${id}`),

  /**
   * Baixa a ajuda publicada como um arquivo.
   *
   * 🔴 Vai por `fetch` com credenciais, e **não** por `<a href>`. Em produção o
   * portal e a API são hostnames irmãos, e o cookie de sessão é `Partitioned`:
   * numa navegação de topo para outro host ele não acompanha, e o download
   * voltaria 401 sem explicação. Pelo `fetch` a requisição sai do mesmo contexto
   * das outras e o cookie vai junto.
   */
  baixarExportacao: async (): Promise<Blob> => {
    const base = import.meta.env.VITE_API_URL ?? ''
    const resposta = await fetch(`${base}/ajuda/exportar`, { credentials: 'include' })

    if (!resposta.ok) throw new ErroApi(resposta.status, null)

    return resposta.blob()
  },
}
